import type { MapData, Vec2, WindowLightDef } from "./types.js";

type LightMap = Pick<MapData, "width" | "height" | "tileSize" | "lightBlockers">;

function blocksLight(map: LightMap, x: number, y: number): boolean {
  return (
    x < 0 ||
    y < 0 ||
    x >= map.width ||
    y >= map.height ||
    map.lightBlockers[y * map.width + x] === 1
  );
}

/**
 * Traverse every tile crossed by a light ray. A wall tile receives light when
 * its nearest surface is visible, but pixels behind it cannot. Corner touches
 * also block light so diagonal rays cannot leak through touching wall tiles.
 */
export function hasLightLineOfSight(map: LightMap, source: Vec2, target: Vec2): boolean {
  const size = map.tileSize;
  let x = Math.floor(source.x / size);
  let y = Math.floor(source.y / size);
  const targetX = Math.floor(target.x / size);
  const targetY = Math.floor(target.y / size);
  if (
    x < 0 ||
    y < 0 ||
    x >= map.width ||
    y >= map.height ||
    targetX < 0 ||
    targetY < 0 ||
    targetX >= map.width ||
    targetY >= map.height
  )
    return false;

  // Test the near face of a destination wall, rather than rays through its
  // thickness. This keeps a continuous wall face lit without tile-shaped gaps.
  const surface = blocksLight(map, targetX, targetY)
    ? {
        x: Math.max(targetX * size, Math.min((targetX + 1) * size, source.x)),
        y: Math.max(targetY * size, Math.min((targetY + 1) * size, source.y)),
      }
    : target;
  // A source exactly on a wall edge can coincide with its nearest surface.
  // Step infinitesimally into the destination tile in that axis so traversal
  // still terminates and correctly visits walls along a grazing ray.
  const dx = surface.x - source.x || (targetX - x) * Number.EPSILON * size;
  const dy = surface.y - source.y || (targetY - y) * Number.EPSILON * size;
  const stepX = Math.sign(dx);
  const stepY = Math.sign(dy);
  const deltaX = dx === 0 ? Infinity : size / Math.abs(dx);
  const deltaY = dy === 0 ? Infinity : size / Math.abs(dy);
  let nextX = dx === 0 ? Infinity : ((x + (stepX > 0 ? 1 : 0)) * size - source.x) / dx;
  let nextY = dy === 0 ? Infinity : ((y + (stepY > 0 ? 1 : 0)) * size - source.y) / dy;

  while (x !== targetX || y !== targetY) {
    if (Math.abs(nextX - nextY) < 1e-10) {
      if (blocksLight(map, x + stepX, y) || blocksLight(map, x, y + stepY)) {
        return x + stepX === targetX && y + stepY === targetY && blocksLight(map, targetX, targetY);
      }
      x += stepX;
      y += stepY;
      nextX += deltaX;
      nextY += deltaY;
    } else if (nextX < nextY) {
      x += stepX;
      nextX += deltaX;
    } else {
      y += stepY;
      nextY += deltaY;
    }
    if (blocksLight(map, x, y)) return x === targetX && y === targetY;
  }
  return true;
}

/** Sample the window aperture from its interior edge, beyond its own wall. */
export function windowLightOrigin(map: LightMap, window: WindowLightDef, targetX: number): Vec2 {
  const { x, y, w, h } = window.rect;
  const source = {
    x: Math.max(x + 0.5, Math.min(x + w - 0.5, targetX)),
    y: window.direction === "down" ? y + h : y,
  };
  const tileY = Math.floor(source.y / map.tileSize);
  if (blocksLight(map, Math.floor(source.x / map.tileSize), tileY)) {
    source.y =
      window.direction === "down" ? (tileY + 1) * map.tileSize + 0.5 : tileY * map.tileSize - 0.5;
  }
  return source;
}
